import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';

export type BreadcrumbItem = {
  label: string;
  routerLink?: string | any[];
  queryParams?: Record<string, any>;
  fragment?: string;
  state?: any;
  bold?: boolean;
};

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [NgFor, NgIf, RouterLink, RouterLinkActive, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="bc" aria-label="Breadcrumb">
      <a
        class="bc__home"
        aria-label="Home"
        [routerLink]="['/']"
        routerLinkActive="is-active"
      >
        <mat-icon aria-hidden="true">home</mat-icon>
      </a>

      <!-- Only render list if there are items -->
      <ng-container *ngIf="items.length > 0">
        <ol class="bc__list">
          <li class="bc__sep" aria-hidden="true">›</li>

          <ng-container
            *ngFor="let item of items; let i = index; trackBy: trackByIndex"
          >
            <li class="bc__item">
              <ng-container *ngIf="item.routerLink; else textOnly">
                <a
                  class="bc__link"
                  [class.bc__bold]="item.bold"
                  [routerLink]="item.routerLink"
                  [queryParams]="item.queryParams || null"
                  [state]="item.state"
                  routerLinkActive="is-active"
                  [attr.aria-current]="i === items.length - 1 ? 'page' : null"
                >
                  {{ item.label }}
                </a>
              </ng-container>
              <ng-template #textOnly>
                <span
                  class="bc__text"
                  [class.bc__bold]="item.bold"
                  [attr.aria-current]="i === items.length - 1 ? 'page' : null"
                  >{{ item.label }}</span
                >
              </ng-template>
            </li>

            <li *ngIf="i < items.length - 1" class="bc__sep" aria-hidden="true">
              ›
            </li>
          </ng-container>
        </ol>
      </ng-container>
    </nav>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .bc {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        line-height: 1;
        color: var(--bc-fg, #374151);
      }
      .bc__home {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        text-decoration: none;
        color: inherit;
        transition: background-color 0.15s ease;
      }
      .bc__home:hover {
        background: rgba(0, 0, 0, 0.06);
      }
      .bc__home .mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      .bc__list {
        list-style: none;
        display: inline-flex;
        padding: 0;
        margin: 0;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
      }
      .bc__item {
        display: inline-flex;
        align-items: center;
        max-width: 40ch;
      }
      .bc__link {
        text-decoration: none;
        color: var(--bc-link, #2563eb);
        white-space: nowrap;
      }
      .bc__link:hover {
        text-decoration: underline;
      }
      .bc__text {
        color: var(--bc-muted, #6b7280);
        white-space: nowrap;
      }
      .bc__bold {
        font-weight: 600;
        color: var(--bc-current, #111827);
      }
      .bc__sep {
        color: var(--bc-sep, #9ca3af);
        user-select: none;
      }
      .is-active[aria-current='page'] {
        pointer-events: none;
        text-decoration: none;
        color: var(--bc-current, #111827);
      }
    `,
  ],
})
export class BreadcrumbsComponent {
  @Input() items: BreadcrumbItem[] = [];

  trackByIndex = (i: number) => i;
}
